import * as THREE from 'three';
import type { BoardRect, Viewport } from '../play/layout';
import type { Place } from '../content/manifest';
import { createPaperTexture } from '../fx/paperTexture';
import { GRADE_GLSL, gradeUniforms, setGradeUniforms, type Grade } from '../fx/glow';
import { easeOutCubic } from '../fx/tween';
import { MIN_HIT_RADIUS_PX } from './PopupScene';

// 小镇地图（spec §6）：透视相机下的一张平面；热点叠盖纸 / 半透明盖纸 / 发光精灵；整体 ±6° 倾斜。
// 世界单位 = CSS px，原点在画布中心，y 向上（与 BoardScene / PopupScene 相同），
// 所以 z=0 平面在未倾斜时与屏幕逐像素对应，终章可以无缝交给 PopupScene。

/** 地图页顶栏（毛毡顶栏 96 + 间距） */
export const MAP_TOP = 104;
/** 地图页底部（进度胶囊 35 + 边距） */
export const MAP_BOTTOM = 66;
const MAP_SIDE = 10;
const FOV = 30;
const DEG = Math.PI / 180;
export const MAP_MAX_TILT = 6;
/** 盖纸比热点圆大一圈 */
const COVER_SCALE = 1.3;
const TEAR_MS = 1100;
const UNVEIL_MS = 700;

export type HotspotState = 'locked' | 'unlocked' | 'completed';

/**
 * 盖纸样式。paper = 米色卡纸（城市地图）；night = 深蓝夜色薄纱（塔罗星图这类深色地图：
 * 米色圆片压在夜空上太重，换成和天空同色、略小的一片，锁着的星"还没亮"）。
 */
export type CoverStyle = 'paper' | 'night';
const COVER_STYLES: Record<CoverStyle, { paper: [number, number, number]; rim: [number, number, number]; scale: number; shadow: number; unlockedA: number }> = {
  paper: { paper: [0.945, 0.905, 0.825], rim: [0.99, 0.975, 0.94], scale: COVER_SCALE, shadow: 1, unlockedA: 0.62 },
  night: { paper: [0.12, 0.13, 0.36], rim: [0.42, 0.4, 0.68], scale: 0.95, shadow: 0, unlockedA: 0.5 },
};



/** 地图在世界里的矩形（w × h，h = w / aspect），装在顶栏与底部胶囊之间 */
export function mapRect(vp: Viewport, aspect: number): BoardRect {
  const top = MAP_TOP + (vp.safeTop ?? 0);
  const bottom = MAP_BOTTOM + (vp.safeBottom ?? 0);
  const availW = vp.width - MAP_SIDE * 2;
  const availH = Math.max(100, vp.height - top - bottom);
  const w = Math.floor(Math.min(availW, availH * aspect));
  return { cx: 0, cy: (bottom - top) / 2, w, h: w / aspect };
}

const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const MAP_FRAG = /* glsl */ `
uniform sampler2D map;
varying vec2 vUv;
${GRADE_GLSL}
void main() {
  vec3 c = texture2D(map, vUv).rgb;
  // 夜里四角略暗（纸面受台灯光的感觉），白天几乎没有
  float v = 1.0 - (1.0 - gradeBright) * 0.6 * smoothstep(0.35, 0.75, length(vUv - 0.5));
  gl_FragColor = vec4(applyGrade(c) * v, 1.0);
  #include <colorspace_fragment>
}`;

const NOISE_GLSL = /* glsl */ `
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y);
}
float fbm(vec2 p) {
  float s = 0.0; float a = 0.5;
  for (int i = 0; i < 5; i++) { s += a * noise(p); p = p * 2.03 + 17.1; a *= 0.5; }
  return s;
}`;

// 盖纸（v2 spec §1：干净纸片，无颗粒）：低频起伏的剪纸边 + 平整纸面 + 极淡的明暗 + 一圈干净的浅色切口 + 外圈柔和落影。
// 只用低频噪声塑造轮廓（形状），纸面本身不加任何噪声纹理。half = 0 整张；±1 = 撕开时的一半（沿一条折线分开）
const COVER_FRAG = /* glsl */ `
uniform float seed;
uniform float opacity;
uniform float halfSide;
uniform vec2 tearDir;
uniform float shadowAmt;
uniform vec3 paperColor;
uniform vec3 rimColor;
varying vec2 vUv;
${GRADE_GLSL}
${NOISE_GLSL}
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  vec2 dir = p / max(r, 1e-4);
  // 剪纸边：单位圆上的低频噪声（周期连续，无接缝），没有高频毛刺
  float edge = 0.76 + 0.12 * (fbm(dir * 1.6 + seed) - 0.5);
  float inside = 1.0 - smoothstep(edge - 0.01, edge, r);
  // 撕开的一半：平缓的折线分界
  float cut = 1.0;
  float rimCut = 0.0;
  if (abs(halfSide) > 0.5) {
    vec2 n = vec2(-tearDir.y, tearDir.x);
    float along = dot(p, tearDir);
    float d = dot(p, n) + 0.08 * (noise(vec2(along * 2.5, seed)) - 0.5);
    float sd = d * halfSide;
    cut = smoothstep(-0.008, 0.004, sd);
    rimCut = 1.0 - smoothstep(0.0, 0.03, sd);
  }
  float a = inside * cut;
  // 右下方的落影（盖纸略微翘起）
  float rs = length(p - vec2(0.045, -0.06));
  float sh = (1.0 - smoothstep(edge - 0.04, edge + 0.1, rs)) * (1.0 - inside) * shadowAmt * (abs(halfSide) > 0.5 ? 0.0 : 1.0);
  // 纸面：平整的米色卡纸，左上受光、右下极淡地暗一点
  vec3 paper = paperColor * (1.0 + 0.035 * dot(p, normalize(vec2(-1.0, 1.0))));
  // 切口：靠边一圈干净的浅色（卡纸的切面），宽度均匀
  float rim = smoothstep(edge - 0.05, edge - 0.012, r);
  rim = max(rim, rimCut);
  paper = mix(paper, rimColor, rim * 0.8);
  vec3 col = applyGrade(paper);
  float outA = max(a * opacity, sh * 0.42 * opacity);
  if (outA < 0.003) discard;
  vec3 rgb = a > 0.01 ? col : vec3(0.27, 0.18, 0.08) * gradeBright;
  gl_FragColor = vec4(rgb, outA);
  #include <colorspace_fragment>
}`;

type Cover = {
  place: Place;
  whole: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  halves: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>[];
  glow: THREE.Sprite;
  state: HotspotState;
  /** 揭纸动画进度（-1 = 没在播） */
  tear: number;
  /** 盖纸透明度过渡（锁 → 解锁） */
  unveil: number;
  shake: number;
  seed: number;
};

export class MapScene {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(FOV, 1, 1, 5000);
  private readonly group = new THREE.Group();
  private readonly plane: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  private readonly bg: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  private readonly covers: Cover[] = [];
  private readonly raycaster = new THREE.Raycaster();
  private readonly coverGeo = new THREE.PlaneGeometry(1, 1);
  private vp: Viewport = { width: 1, height: 1 };
  private rect: BoardRect = { cx: 0, cy: 0, w: 1, h: 1 };
  private tilt = { x: 0, y: 0 };
  private time = 0;
  private glowAmt = 0.55;

  constructor(
    private readonly texture: THREE.Texture,
    readonly aspect: number,
    places: Place[],
    states: Record<string, HotspotState>,
    private readonly glowTexture: THREE.Texture,
    readonly coverStyle: CoverStyle = 'paper',
  ) {
    const cs = COVER_STYLES[coverStyle];
    const paper = createPaperTexture();
    this.bg = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ map: paper, depthTest: false, depthWrite: false }));
    this.bg.renderOrder = 0;
    this.plane = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: MAP_FRAG, depthTest: false, depthWrite: false, uniforms: { map: { value: texture }, ...gradeUniforms() } }),
    );
    this.plane.renderOrder = 1;
    this.group.add(this.plane);
    places.forEach((place, i) => {
      const seed = 3.1 + i * 7.77;
      const mk = (half: number, ro: number) => {
        const m = new THREE.Mesh(
          this.coverGeo,
          new THREE.ShaderMaterial({
            vertexShader: VERT,
            fragmentShader: COVER_FRAG,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            uniforms: {
              seed: { value: seed },
              opacity: { value: 1 },
              halfSide: { value: half },
              tearDir: { value: new THREE.Vector2(Math.cos(0.9 + i), Math.sin(0.9 + i)) },
              shadowAmt: { value: cs.shadow },
              paperColor: { value: new THREE.Color(...cs.paper) },
              rimColor: { value: new THREE.Color(...cs.rim) },
              ...gradeUniforms(),
            },
          }),
        );
        m.renderOrder = ro;
        this.group.add(m);
        return m;
      };
      const glow = new THREE.Sprite(
        new THREE.SpriteMaterial({ map: glowTexture, color: 0xffb54d, transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending }),
      );
      glow.renderOrder = 20;
      this.group.add(glow);
      const state = states[place.id] ?? 'locked';
      const c: Cover = { place, whole: mk(0, 10), halves: [mk(1, 11), mk(-1, 11)], glow, state, tear: -1, unveil: 1, shake: 0, seed };
      this.covers.push(c);
    });
    this.scene.add(this.bg, this.group);
  }

  get mapRect(): BoardRect {
    return this.rect;
  }

  setViewport(vp: Viewport): void {
    this.vp = vp;
    this.rect = mapRect(vp, this.aspect);
    this.camera.aspect = vp.width / vp.height;
    this.camera.fov = FOV;
    const dist = vp.height / 2 / Math.tan((FOV * DEG) / 2);
    this.camera.position.set(0, 0, dist);
    this.camera.near = Math.max(1, dist - 2000);
    this.camera.far = dist + 2000;
    this.camera.updateProjectionMatrix();
    // 首帧渲染前也要能投影 / 射线命中（matrixWorldInverse 平时由 render 更新）
    this.camera.updateMatrixWorld(true);
    const sw = vp.width * 1.4;
    const sh = vp.height * 1.4;
    this.bg.scale.set(sw, sh, 1);
    this.bg.position.z = -60;
    const map = this.bg.material.map!;
    map.repeat.set(sw / 256, sh / 256);
    this.apply();
  }

  setState(id: string, state: HotspotState, animate: boolean): void {
    const c = this.covers.find((x) => x.place.id === id);
    if (!c || c.state === state) return;
    const prev = c.state;
    c.state = state;
    if (!animate) return;
    if (state === 'completed') c.tear = 0;
    else if (state === 'unlocked' && prev === 'locked') c.unveil = 0;
  }

  /** 揭纸动画是否还在播 */
  get animating(): boolean {
    return this.covers.some((c) => (c.tear >= 0 && c.tear < TEAR_MS) || c.unveil < 1);
  }

  shake(id: string): void {
    const c = this.covers.find((x) => x.place.id === id);
    if (c) c.shake = 1;
  }

  update(dtMs: number, tilt: { x: number; y: number }, grade: Grade): void {
    this.time += dtMs;
    this.tilt = tilt;
    this.glowAmt = grade.glow;
    setGradeUniforms(this.plane.material.uniforms, grade);
    this.bg.material.color.copy(grade.tint).multiplyScalar(grade.bright);
    for (const c of this.covers) {
      for (const m of [c.whole, ...c.halves]) setGradeUniforms(m.material.uniforms, grade);
      if (c.tear >= 0 && c.tear < TEAR_MS) c.tear = Math.min(TEAR_MS, c.tear + dtMs);
      if (c.unveil < 1) c.unveil = Math.min(1, c.unveil + dtMs / UNVEIL_MS);
      if (c.shake > 0) c.shake = Math.max(0, c.shake - dtMs / 380);
    }
    this.apply();
  }

  private apply(): void {
    const W = this.rect.w;
    const H = W / this.aspect;
    this.group.position.set(this.rect.cx, this.rect.cy, 0);
    this.group.rotation.set(this.tilt.x * DEG, this.tilt.y * DEG, 0);
    this.plane.scale.set(W, H, 1);
    for (const c of this.covers) {
      const h = c.place.hotspot;
      const x = (h.x - 0.5) * W;
      const y = (0.5 - h.y) * H;
      const cs = COVER_STYLES[this.coverStyle];
      const size = Math.max(h.r * W, MIN_HIT_RADIUS_PX) * 2 * cs.scale;
      const shakeX = c.shake > 0 ? Math.sin(c.shake * 40) * 5 * c.shake : 0;
      // 盖纸：锁着 = 不透明；解锁未完成 = 半透明露出轮廓；完成 = 撕开（只首次播放）或直接没有
      const lockedA = 1;
      const unlockedA = cs.unlockedA;
      let wholeA = 0;
      if (c.state === 'locked') wholeA = lockedA;
      else if (c.state === 'unlocked') wholeA = lockedA + (unlockedA - lockedA) * easeOutCubic(c.unveil);
      const tearing = c.state === 'completed' && c.tear >= 0 && c.tear < TEAR_MS;
      c.whole.visible = wholeA > 0 && !tearing;
      c.whole.position.set(x + shakeX, y, 1);
      c.whole.scale.set(size, size, 1);
      c.whole.rotation.z = 0;
      c.whole.material.uniforms.opacity.value = wholeA;
      // 撕开：两半先往外绷一下，再分别向斜上方 / 斜下方飞出屏幕、旋转、淡出
      c.halves.forEach((m, i) => {
        m.visible = tearing;
        if (!tearing) return;
        const k = c.tear / TEAR_MS;
        const pull = Math.min(1, k / 0.18);
        const fly = Math.max(0, (k - 0.18) / 0.82);
        const e = fly * fly;
        const s = i === 0 ? 1 : -1;
        const d = m.material.uniforms.tearDir.value as THREE.Vector2;
        const nx = -d.y * s;
        const ny = d.x * s;
        m.position.set(x + nx * (4 * pull + 420 * e), y + ny * (4 * pull + 420 * e) + 260 * e, 2 + 40 * e);
        m.rotation.z = s * (0.06 * pull + 2.2 * e);
        m.scale.set(size * (1 + 0.15 * e), size * (1 + 0.15 * e), 1);
        m.material.uniforms.opacity.value = 1 - Math.max(0, (fly - 0.55) / 0.45);
      });
      // 发光：完成的地点；撕开过程中逐渐亮起
      const lit = c.state === 'completed' ? (c.tear >= 0 ? easeOutCubic(Math.min(1, c.tear / TEAR_MS)) : 1) : 0;
      const pulse = 1 + 0.07 * Math.sin(this.time / 450 + c.seed);
      const gs = h.r * W * 3.0 * pulse;
      c.glow.visible = lit > 0.001;
      c.glow.position.set(x, y, 3);
      c.glow.scale.set(gs, gs, 1);
      c.glow.material.opacity = Math.min(1, lit * this.glowAmt);
    }
  }

  // ── 命中 ──

  /** 屏幕点（CSS px）射线 → 地图归一化坐标（x 向右、y 向下，0..1） */
  pointOnMap(sx: number, sy: number): { x: number; y: number } | null {
    const ndc = new THREE.Vector2((sx / this.vp.width) * 2 - 1, -((sy / this.vp.height) * 2 - 1));
    this.raycaster.setFromCamera(ndc, this.camera);
    this.plane.updateMatrixWorld(true);
    const normal = new THREE.Vector3(0, 0, 1).transformDirection(this.plane.matrixWorld);
    const point = new THREE.Vector3().setFromMatrixPosition(this.plane.matrixWorld);
    const hit = this.raycaster.ray.intersectPlane(new THREE.Plane().setFromNormalAndCoplanarPoint(normal, point), new THREE.Vector3());
    if (!hit) return null;
    const local = this.plane.worldToLocal(hit);
    return { x: local.x + 0.5, y: 0.5 - local.y };
  }

  /** 地图坐标 → 屏幕 CSS px */
  screenOf(x: number, y: number): { x: number; y: number } {
    this.plane.updateMatrixWorld(true);
    const p = new THREE.Vector3(x - 0.5, 0.5 - y, 0).applyMatrix4(this.plane.matrixWorld).project(this.camera);
    return { x: ((p.x + 1) / 2) * this.vp.width, y: ((1 - p.y) / 2) * this.vp.height };
  }

  /** 点中的热点（最近的那个）；命中半径 = max(热点 r, 屏幕 22px) */
  hit(sx: number, sy: number): { id: string; state: HotspotState; dist: number; radius: number } | null {
    const m = this.pointOnMap(sx, sy);
    if (!m) return null;
    const W = this.rect.w;
    const H = W / this.aspect;
    let best: { id: string; state: HotspotState; dist: number; radius: number } | null = null;
    for (const c of this.covers) {
      const h = c.place.hotspot;
      // 在地图像素里量（各向同性）；屏幕最小半径按当前缩放换算回地图
      const ppu = this.pxScale(h.x, h.y);
      const dist = Math.hypot((m.x - h.x) * W, (m.y - h.y) * H);
      const radius = Math.max(h.r * W, MIN_HIT_RADIUS_PX / ppu);
      if (dist <= radius && (!best || dist < best.dist)) best = { id: c.place.id, state: c.state, dist, radius };
    }
    return best;
  }

  /** 地图 1 世界 px 在屏幕上是多少 px（倾斜时略变） */
  private pxScale(x: number, y: number): number {
    const a = this.screenOf(x, y);
    const b = this.screenOf(x + 10 / this.rect.w, y);
    return Math.hypot(b.x - a.x, b.y - a.y) / 10;
  }

  hotspotScreen(id: string): { x: number; y: number; r: number } | null {
    const c = this.covers.find((x) => x.place.id === id);
    if (!c) return null;
    const p = this.screenOf(c.place.hotspot.x, c.place.hotspot.y);
    return { ...p, r: c.place.hotspot.r * this.rect.w * this.pxScale(c.place.hotspot.x, c.place.hotspot.y) };
  }

  stateOf(id: string): HotspotState | null {
    return this.covers.find((x) => x.place.id === id)?.state ?? null;
  }

  dispose(): void {
    this.plane.geometry.dispose();
    this.plane.material.dispose();
    this.bg.geometry.dispose();
    this.bg.material.map?.dispose();
    this.bg.material.dispose();
    this.coverGeo.dispose();
    for (const c of this.covers) {
      c.whole.material.dispose();
      c.halves.forEach((h) => h.material.dispose());
      c.glow.material.dispose();
    }
    this.texture.dispose();
    this.scene.clear();
    void this.glowTexture; // 发光纹理由 App 共享持有
  }
}
